(() => {
  const STORAGE_KEY = 'theme:wishlist';

  const state = {
    items: [],
    panel: null,
    panelItems: null,
    panelEmpty: null,
    panelCount: null,
    headerCount: null,
    toast: null,
    toastTitle: null,
    toastProduct: null,
    toastView: null,
    toastTimer: null,
    toastHideTimer: null,
    panelOpen: false,
  };

  const escapeHTML = (value = '') =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');

  const getItems = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const saveItems = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
    document.dispatchEvent(
      new CustomEvent('wishlist:update', { detail: { items: state.items } })
    );
  };

  const findIndexByKey = (key) =>
    state.items.findIndex((item) => item.key === key);

  const isSaved = (key) => findIndexByKey(key) !== -1;

  const buildKey = (productId, variantId) =>
    `${String(productId || '')}:${String(variantId || '')}`;

  const syncRefs = () => {
    state.panel = document.querySelector('[data-wishlist-panel]');
    state.panelItems = document.querySelector('[data-wishlist-items]');
    state.panelEmpty = document.querySelector('[data-wishlist-empty]');
    state.panelCount = document.querySelector('[data-wishlist-panel-count]');
    state.headerCount = document.querySelector('[data-wishlist-count]');
    state.toast = document.querySelector('[data-wishlist-toast]');
    state.toastTitle = document.querySelector('[data-wishlist-toast-title]');
    state.toastProduct = document.querySelector('[data-wishlist-toast-product]');
    state.toastView = document.querySelector('[data-wishlist-toast-view]');

    if (state.toast && state.panel && state.panel.contains(state.toast)) {
      document.body.appendChild(state.toast);
    }
  };

  const updateCounts = () => {
    const count = state.items.length;

    if (state.headerCount) {
      state.headerCount.textContent = String(count);
      state.headerCount.hidden = count === 0;
    }

    if (state.panelCount) {
      state.panelCount.textContent = String(count);
    }
  };

  const syncToggleButtons = () => {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const key = buildKey(button.dataset.productId, button.dataset.variantId);
      const active = isSaved(key);

      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute(
        'aria-label',
        active ? 'Remove from wishlist' : 'Add to wishlist'
      );
    });
  };

  const renderPanel = () => {
    if (!state.panelItems || !state.panelEmpty) return;

    if (!state.items.length) {
      state.panelEmpty.hidden = false;
      state.panelItems.innerHTML = '';
      updateCounts();
      syncToggleButtons();
      return;
    }

    state.panelEmpty.hidden = true;

    state.panelItems.innerHTML = state.items
      .map(
        (item) => `
          <article class="wishlist-item" data-wishlist-product-id="${escapeHTML(item.productId)}" data-item-key="${escapeHTML(item.key)}">
            <a class="wishlist-item__media" href="${escapeHTML(item.url)}">
              ${
                item.image
                  ? `<img src="${escapeHTML(item.image)}" alt="${escapeHTML(item.title)}" loading="lazy">`
                  : ''
              }
            </a>

            <div class="wishlist-item__content">
              <h3 class="wishlist-item__title">
                <a href="${escapeHTML(item.url)}">${escapeHTML(item.title)}</a>
              </h3>

              ${item.variantTitle ? `<p class="wishlist-item__date">${escapeHTML(item.variantTitle)}</p>` : ''}

              ${item.priceText ? `<p class="wishlist-item__meta">${escapeHTML(item.priceText)}</p>` : ''}

              <div class="wishlist-item__actions">
                <button
                  type="button"
                  class="wishlist-item__cart"
                  data-wishlist-native-cart
                >
                  Add to cart
                </button>

                <button
                  type="button"
                  class="wishlist-item__remove"
                  data-wishlist-remove="${escapeHTML(item.key)}"
                  aria-label="Remove item"
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8">
                    <path d="M4 7h16"/>
                    <path d="M10 11v6"/>
                    <path d="M14 11v6"/>
                    <path d="M6 7l1 12h10l1-12"/>
                    <path d="M9 7V5h6v2"/>
                  </svg>
                </button>
              </div>

              <form class="wishlist-native-cart-form" hidden>
                <input type="hidden" name="id" value="${escapeHTML(item.variantId)}">
              </form>
            </div>
          </article>
        `
      )
      .join('');

    updateCounts();
    syncToggleButtons();
  };

  const openPanel = () => {
    if (!state.panel) return;
    state.panel.hidden = false;
    state.panelOpen = true;

    requestAnimationFrame(() => {
      state.panel.classList.add('is-open');
    });

    document.body.style.overflow = 'hidden';
  };

  const closePanel = () => {
    if (!state.panel) return;
    state.panel.classList.remove('is-open');
    state.panelOpen = false;

    window.setTimeout(() => {
      if (state.panel) state.panel.hidden = true;
      document.body.style.overflow = '';
    }, 280);
  };

  const hideToast = () => {
    if (!state.toast) return;

    clearTimeout(state.toastTimer);
    clearTimeout(state.toastHideTimer);

    state.toast.classList.remove('is-entering');
    state.toast.classList.add('is-exiting');

    state.currentToastKey = null;

    state.toastHideTimer = window.setTimeout(() => {
      if (state.toast) state.toast.hidden = true;
    }, 240);
  };

  const showToast = (item) => {
    if (!state.toast || !state.toastTitle || !state.toastProduct) return;

    clearTimeout(state.toastTimer);
    clearTimeout(state.toastHideTimer);

    const key = buildKey(item.productId, item.variantId);
    state.currentToastKey = key;

    state.toastTitle.textContent = 'Product added to wishlist successfully';

    state.toastProduct.innerHTML = `
      <div class="wishlist-toast__body">
        ${
          item.image
            ? `<img src="${escapeHTML(item.image)}" alt="${escapeHTML(item.title)}" loading="lazy">`
            : ''
        }
        <div>
          <p class="wishlist-toast__product-name">${escapeHTML(item.title)}</p>
        </div>
      </div>
    `;

    state.toast.hidden = false;
    state.toast.classList.remove('is-exiting');

    requestAnimationFrame(() => {
      state.toast.classList.add('is-entering');
    });

    state.toastTimer = window.setTimeout(() => {
      hideToast();
    }, 5000);
  };

  const addItem = (item) => {
    const key = buildKey(item.productId, item.variantId);
    if (!item.productId || !item.variantId) return;

    if (isSaved(key)) {
      state.items = state.items.filter((entry) => entry.key !== key);
      saveItems();
      renderPanel();
      return;
    }

    state.items.unshift({
      key,
      productId: String(item.productId),
      variantId: String(item.variantId),
      title: item.title || 'Product',
      url: item.url || '#',
      image: item.image || '',
      variantTitle: item.variantTitle || '',
      priceText: item.priceText || '',
      addedAt: new Date().toISOString(),
    });

    saveItems();
    renderPanel();
    showToast(item);
  };

  const isToastRelatedToKey = (key) => {
    if (!state.toastProduct) return false;

    const currentTitle = state.toastProduct.textContent || '';
    const item = state.items.find((i) => i.key === key);

    if (!item) return false;

    return currentTitle.includes(item.title);
  };

  const removeItem = (key) => {
    const next = state.items.filter((item) => item.key !== key);
    state.items = next;

    saveItems();
    renderPanel();

    if (state.currentToastKey === key) {
      hideToast();
    }
  };

  const clearAll = () => {
    if (!state.items.length) return;
    state.items = [];
    saveItems();
    renderPanel();
  };

  const addAllToCart = async () => {
    // placeholder hook for future bulk add
    // keep UI ready without breaking current flow
    return Promise.resolve();
  };

  const addWishlistItemToCart = async (card) => {
    const variantId = card?.querySelector('input[name="id"]')?.value;
    if (!variantId) return;

    const button = card.querySelector('[data-wishlist-native-cart]');
    const spinner = button?.querySelector('.loading__spinner');

    try {
      if (button) button.setAttribute('disabled', 'disabled');
      if (spinner) spinner.classList.remove('hidden');

      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
        }),
      });

      const data = await response.json();
      if (data.status) return;

      document.dispatchEvent(
        new CustomEvent('cart:refresh', { detail: { response: data } })
      );

      closePanel();
    } catch (error) {
      console.error(error);
    } finally {
      if (button) button.removeAttribute('disabled');
      if (spinner) spinner.classList.add('hidden');
    }
  };

  const bindEvents = () => {
    document.addEventListener('click', (event) => {
      const toggleButton = event.target.closest('[data-wishlist-toggle]');
      if (toggleButton) {
        event.preventDefault();

        addItem({
          productId: toggleButton.dataset.productId,
          variantId: toggleButton.dataset.variantId,
          title: toggleButton.dataset.productTitle,
          url: toggleButton.dataset.productUrl,
          image: toggleButton.dataset.productImage,
          variantTitle: toggleButton.dataset.variantTitle,
          priceText: toggleButton.dataset.productPriceText,
        });

        return;
      }

      const openButton = event.target.closest('[data-wishlist-open]');
      if (openButton) {
        event.preventDefault();
        openPanel();
        return;
      }

      const closeButton = event.target.closest('[data-wishlist-close]');
      if (closeButton) {
        event.preventDefault();
        closePanel();
        return;
      }

      const removeButton = event.target.closest('[data-wishlist-remove]');
      if (removeButton) {
        event.preventDefault();

        const confirmRemove = window.confirm('Remove this item from your wishlist?');
        if (!confirmRemove) return;

        removeItem(removeButton.dataset.wishlistRemove);

        return;
      }

      const clearButton = event.target.closest('[data-wishlist-clear]');
      if (clearButton) {
        event.preventDefault();
        clearAll();
        return;
      }

      const addAllButton = event.target.closest('[data-wishlist-add-all]');
      if (addAllButton) {
        event.preventDefault();
        addAllToCart();
        return;
      }

      const toastClose = event.target.closest('[data-wishlist-toast-close]');
      if (toastClose) {
        event.preventDefault();
        hideToast();
        return;
      }

      const toastView = event.target.closest('[data-wishlist-toast-view]');
      if (toastView) {
        event.preventDefault();
        hideToast();
        openPanel();
        return;
      }

      const nativeCartButton = event.target.closest('[data-wishlist-native-cart]');
      if (nativeCartButton) {
        event.preventDefault();
        const card = nativeCartButton.closest('.wishlist-item');
        addWishlistItemToCart(card);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && state.panelOpen) {
        closePanel();
      }
    });

    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY) return;
      state.items = getItems();
      renderPanel();
    });

    document.addEventListener('wishlist:remove', (event) => {
      if (!event.detail?.key) return;
      removeItem(event.detail.key);
    });
  };

  const init = () => {
    syncRefs();
    state.items = getItems();
    renderPanel();
    bindEvents();
    updateCounts();
    syncToggleButtons();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();